/**
 * Contact Info Detection and Alert Utility
 *
 * Detects phone numbers, emails, social media handles, and payment apps
 * in messages. When detected:
 * 1. Redacts the contact info with █ characters
 * 2. Sends a safety warning message to both parties
 * 3. Alerts support via Slack
 */

import { findPhoneNumbersInText } from 'libphonenumber-js';
import { ChatDocument, ChatMessagesDocumentSchema } from '@util/types/firestore/chat';
import { arrayUnion, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@util/firebase';

/**
 * GB #1942: senders whose messages are NOT masked: admins, support staff, and
 * admin-designated trusted partners. Strict `=== true` so a truthy-but-wrong
 * value ("true", 1) never grants the exemption.
 *
 * WHY this is only a convenience: the server (gearfocus-api) is the source of
 * truth and re-checks the sender's role itself. Callers must pass the signed-in
 * user's OWN users/{uid} roles (useAuth().userDoc.roles), never anything read
 * from the message or supplied by the client. Faking roles here cannot grant a
 * bypass; firestore.rules blocks clients from writing `roles` on their own doc.
 */
export function isMaskingExempt(
  roles: { admin?: unknown; support?: unknown; trusted_partner?: unknown } | null | undefined
): boolean {
  return (
    roles?.admin === true ||
    roles?.support === true ||
    roles?.trusted_partner === true
  );
}

// API route for contact info alerts (server-side Slack webhook)
const CONTACT_INFO_ALERT_API = '/api/alerts/contact-info';

// Safety warning messages shown to both parties (rendered as banners in MessageBubble)
// These use special prefixes that MessageBubble detects to style as grey banners

// Warning for contact info (phones, emails, @handles)
const CONTACT_INFO_WARNING = `[SAFETY_BANNER]For your safety, we hid details that would move this deal off GearFocus. Scams and payment disputes happen more often when conversations leave the platform. Keeping messages here protects you and keeps a clear record if anything goes wrong.`;

// Warning for payment apps (venmo, zelle, paypal, etc.)
const PAYMENT_APP_WARNING = CONTACT_INFO_WARNING; // one banner for all four masked cases (Bill 2026-10-18)

// Warning for suspicious/phishing URLs (blocked TLDs)
const SUSPICIOUS_URL_WARNING = CONTACT_INFO_WARNING; // one banner for all four masked cases (Bill 2026-10-18)

// Payment apps and services to detect
const PAYMENT_APPS_REGEX = /\b(venmo|zelle|paypal|pay\s*pal|cash\s*app|cashapp)\b/i;

// GB #2055: competitor / off-platform sale sites. MASKED (steering a buyer to
// another site loses the sale and our fee). Brand and two-word forms only: the
// bare word "marketplace" must never match (GearFocus is a marketplace) and
// "offer up" as two words is ordinary English ("offer up a price"). Keep in sync
// with gearfocus-api functions/src/chat/content-detection.ts.
const OFFPLATFORM_SITES_REGEX = /\b(offerup|facebook[\s._-]*marketplace|fb[\s._-]*marketplace|facebook\.com\/marketplace[^\s]*|craigslist|mercari|swappa)\b/i;

// GB #2055: ALERT ONLY. Sellers legitimately compare prices to eBay/KEH/MPB, so
// these are flagged for review but never masked.
const OFFPLATFORM_ALERT_REGEX = /\b(ebay|keh|mpb|text\s+me|call\s+me|meet\s+up)\b/i;

// Hosts whose URLs are NOT redacted. Anything outside this list gets the suspicious-link banner.
// Subdomain hosts: host itself OR any *.host subdomain is allowed.
// Exact hosts: only the exact hostname is allowed (no subdomains).

// Hosts where any subdomain is allowed (host or *.host).
const URL_ALLOWLIST_SUBDOMAIN_HOSTS = [
  'gearfocus.com',
  'usps.com',  // tracking URLs
  'ups.com',   // tracking URLs
  'fedex.com', // tracking URLs
  'youtube.com', // seller brand channels (e.g., Alien Drones)
  'youtu.be',    // YouTube share links
  'bluemark.io', // manufacturer manuals (Remote ID modules)
  'dronescout.co', // Bluemark sister site — product pages / bridge / manuals
];

// Hosts where only the exact host is allowed (no subdomain).
const URL_ALLOWLIST_EXACT_HOSTS = [
  'gearfocus.b-cdn.net',
];

function isAllowlistedUrl(urlStr: string): boolean {
  try {
    const url = new URL(urlStr);
    const host = url.hostname.toLowerCase();
    const hostAllowed =
      URL_ALLOWLIST_EXACT_HOSTS.includes(host) ||
      URL_ALLOWLIST_SUBDOMAIN_HOSTS.some(
        (allowed) => host === allowed || host.endsWith('.' + allowed)
      );
    if (!hostAllowed) return false;
    // Defeat open-redirect smuggling: an allowlisted host whose path or query
    // embeds another URL (e.g. youtube.com/redirect?q=https://evil.com) must NOT
    // be treated as safe. Normalization decodes %XX before this runs, but we
    // also check encoded forms defensively in case a caller passes raw input.
    const rest = (url.pathname + url.search).toLowerCase();
    if (
      rest.includes('//') || // embedded URL or protocol-relative //evil.com
      /https?:/.test(rest) || // embedded scheme incl. single-slash https:/evil.com
      rest.includes('%2f%2f') || // percent-encoded // (if not yet decoded)
      rest.includes('http%3a') // percent-encoded scheme
    ) {
      return false;
    }
    return true;
  } catch {
    return false; // Malformed URL — treat as not-allowlisted (err on the side of safety)
  }
}

// Collects [start, end) ranges for every match of `regex` in `text` using a
// RegExp.exec loop (ES2017-compatible; avoids String.matchAll). The regex is
// re-created with the global flag so exec advances through all matches.
function collectMatchRanges(
  text: string,
  regex: RegExp
): Array<[number, number]> {
  const re = new RegExp(
    regex.source,
    regex.flags.includes('g') ? regex.flags : regex.flags + 'g'
  );
  const ranges: Array<[number, number]> = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    ranges.push([m.index, m.index + m[0].length]);
    if (m[0].length === 0) re.lastIndex++; // guard against zero-width loops
  }
  return ranges;
}

// Overwrites [start, end) with block characters. Length-preserving, so callers
// can apply multiple ranges in sequence without offsets shifting.
function maskRange(text: string, start: number, end: number): string {
  return text.slice(0, start) + '█'.repeat(end - start) + text.slice(end);
}

// True if a "@handle" match at `start` is actually a legitimate YouTube channel
// path segment and so must NOT be redacted as a contact handle. The handle is
// exempt ONLY when it is the first path segment ("/@name") of a real, schemed,
// allowlisted YouTube URL that encloses this position. This parses the enclosing
// URL rather than string-matching, so fakes (notyoutube.com/@x) and query-embedded
// handles (youtube.com/watch?v=youtube.com/@x) are NOT exempt.
function isYouTubeChannelHandle(text: string, start: number): boolean {
  const re = new RegExp(EXTERNAL_URL_REGEX.source, EXTERNAL_URL_REGEX.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (start < m.index || start >= m.index + m[0].length) continue;
    try {
      const u = new URL(m[0]);
      const host = u.hostname.toLowerCase();
      const isYouTubeHost =
        host === 'youtube.com' ||
        host === 'www.youtube.com' ||
        host === 'm.youtube.com' ||
        host === 'youtu.be';
      return isYouTubeHost && /^\/@[a-zA-Z0-9_]{2,30}$/.test(u.pathname);
    } catch {
      return false;
    }
  }
  return false;
}

// Social media handle pattern (@username)
const SOCIAL_HANDLE_REGEX = /@[a-zA-Z0-9_]{2,30}\b/g;

// Email pattern
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

// General URL detection - catches https:// and http:// URLs for allowlist-gated redaction.
// Bare-domain and www. branches removed (Fix 2): only schemed URLs are treated as URLs on both
// client and server. Casual mentions like "amazon.com" no longer trigger the banner.
// Optional userinfo segment (Fix 1): captures user@host so gearfocus.com@evil.com is not bypassed.
const EXTERNAL_URL_REGEX = new RegExp(
  String.raw`https?:\/\/?(?:[^\s@\/]+@)?[\p{L}\p{M}\p{N}][-\p{L}\p{M}\p{N}]*(?:\.[\p{L}\p{M}\p{N}][-\p{L}\p{M}\p{N}]*)+(?:\/[^\s<>"']*)?`,
  "giu"
);

// Social media platform names mentioned (even without URL)
const SOCIAL_PLATFORM_REGEX = /\b(?:find me on|message me on|contact me on|hit me up on|dm me on|reach me on|add me on|follow me on)?\s*(?:facebook|instagram|twitter|tiktok|snapchat|linkedin|whatsapp|telegram|signal|discord)\b/gi;

// Bare-domain URL shorteners commonly weaponized for phishing, plus
// off-platform messaging app shortcuts. Matched WITHOUT requiring http://
// prefix because attackers strip the scheme to bypass EXTERNAL_URL_REGEX
// (e.g., "tr.ee/xyz" or "tr%2eee/xyz" decoded by normalization).
const URL_SHORTENER_DOMAINS = [
  'bit.ly', 'tr.ee', 't.co', 'tinyurl.com', 't.ly', 'ow.ly',
  'rebrand.ly', 'short.io', 'is.gd', 'buff.ly', 'cutt.ly',
  'shorturl.at', 'rb.gy', 's.id', 'v.gd', 'snip.ly', 'bit.do',
  'goo.gl', 'tiny.cc', 't.me', 'wa.me', 'discord.gg',
];

const SHORTENER_REGEX = new RegExp(
  `\\b(?:${URL_SHORTENER_DOMAINS
    .map((d) => d.replace(/\./g, '\\.'))
    .join('|')})\\/[^\\s<>"']*`,
  'gi'
);

// GB #2064: bare "domain.tld/path" links with no scheme (e.g.
// "gearfocus.id8954955.shop/187691623"). A path, query, or fragment is
// REQUIRED: a bare site name with no path ("bhphotovideo.com") is ordinary
// conversation, and every scam link in the web#2056 review had a path. Also
// covers a trailing dot, a :port, a raw IP host, and xn-- (IDN) endings. Group 1
// is the character before the link, so a match never starts inside an email or
// a longer word, while a leading "." "/" "_" or "-" does not hide a link. No
// lookbehind: Safari < 16.4 cannot parse one (see simpleMarkdown.tsx). The path stops at < > " ' so HTML around a link
// (listing descriptions) is never masked, and at , ( ) | ! ; so a second link
// glued on with punctuation ("gearfocus.com/,evil.shop/pay") is matched on its
// own. A "?" query must contain "=", so "a7.III?Thanks" is not a link.
// Keep ordinary links on the existing fast path. IDNA also accepts combining
// marks in final labels, symbols, and punctuation (e.g. IDNA middle dots). ASCII "="/"+"
// stay separators, so normal trusted query strings keep their meaning.
const EXTENDED_IDN_CHAR = /(?=\P{ASCII})[\p{M}\p{P}\p{S}]/u;
const IDN_SYMBOL = /(?=\P{ASCII})[\p{P}\p{S}]/u;
function idnHostRegex(regex: RegExp, text: string): RegExp {
  if (!EXTENDED_IDN_CHAR.test(text)) return regex;
  const source = regex.source
    .replace(/\[\\p\{L\}\]/g, "[\\p{L}\\p{M}]")
    .replace(/\[(\^?)([^\]]*)\]/g, (original, negated: string, chars: string) => {
      if (negated || !chars.includes("\\p{L}")) return original;
      return `(?:${original}|(?=[^\\x00-\\x7F])[\\p{P}\\p{S}])`;
    });
  return new RegExp(source, regex.flags);
}

const BARE_DOMAIN_PATH_REGEX = new RegExp(
  String.raw`(^|[^\p{L}\p{M}\p{N}@])((?:(?:[\p{L}\p{M}\p{N}][-\p{L}\p{M}\p{N}]*\.)+(?:[\p{L}]{2,63}|xn--[a-z0-9-]{1,59})\.?|(?:\d{1,3}\.){3}\d{1,3})(?::\d{1,5})?(?:\/[^\s<>"',()|!;\u2026]*|\?[a-z0-9_%.-]*=[^\s<>"',()|!;\u2026]*|#[a-z0-9=&%_-][^\s<>"',()|!;\u2026]*))`,
  "giu"
);

// GB #2064: a host smuggled inside an allowlisted bare link. Counts: a query or
// fragment VALUE that is a domain, xn-- domain, or IP (youtube.com/redirect?q=
// evil.shop), and a PATH segment that is a domain with a common or scam-used
// ending, an xn-- domain, or an IP (youtube.com/@x/evil.shop/pay, /1.2.3.4/pay). File names and model slugs (photo.jpg?w=800,
// /m/a7.iii/specs) are not domains; allowlisted values (feature=youtu.be) are fine.
// Unicode host letters must be treated exactly like their punycode spelling.
// Query values also carry ordinary image/document filenames on trusted links.
const FILE_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "gif", "webp", "avif", "heic", "heif", "bmp", "tif", "tiff", "svg",
  "pdf", "doc", "docx", "txt", "rtf", "odt", "xls", "xlsx", "csv", "ppt", "pptx",
]);
const HOST_SRC =
  '(?:[\\p{L}\\p{M}\\p{N}][-\\p{L}\\p{M}\\p{N}]*\\.)+(?:[\\p{L}]{2,63}|xn--[a-z0-9-]{1,59})|(?:\\d{1,3}\\.){3}\\d{1,3}';
const COMMON_TLDS = [
  'com', 'net', 'org', 'io', 'co', 'me', 'ly', 'ee', 'be', 'gg', 'to', 'link', 'click', 'shop', 'store', 'sbs', 'cfd', 'top', 'xyz', 'site', 'online', 'info', 'biz', 'live', 'app', 'world',
  'icu', 'cyou', 'bond', 'lol', 'vip', 'win', 'buzz', 'rest', 'fun', 'life', 'club', 'work', 'email', 'support', 'help', 'page', 'pro', 'tech', 'space', 'website', 'today', 'news',
  'cc', 'tv', 'ws', 'pw', 'tk', 'ml', 'ga', 'cf', 'gq', 'su', 'ru', 'cn', 'uk', 'us', 'ca', 'de', 'fr', 'es', 'it', 'nl', 'au', 'jp', 'br', 'in', 'eu', 'pl', 'ua', 'kz', 'vn', 'id', 'ph', 'sg', 'hk', 'tw', 'kr', 'ch', 'se', 'tr', 'ng', 'za', 'ae', 'mx', 'ar',
  // Bill 2026-10-19 16:45: "(b) Widen to the scam-used endings". Cheap endings
  // scam operators use (CTO M35). .mov and .zip are left out: they are file
  // extensions in our own CDN links.
  'cam', 'mom', 'beauty', 'quest', 'skin', 'hair', 'makeup', 'autos', 'boats', 'homes', 'yachts', 'motorcycles',
  'pics', 'monster', 'best', 'wiki', 'ink', 'one', 'zone', 'ai', 'dev', 'cloud', 'market', 'deals',
  'sale', 'money', 'finance', 'digital', 'photo', 'camera', 'studio', 'bar', 'lat', 'bid', 'loan', 'racing',
  'date', 'men', 'review', 'stream', 'download', 'trade', 'party', 'science', 'faith', 'accountant', 'cricket', 'gdn',
  'kim', 'country', 'uno', 'ooo', 'xin', 'mobi', 'asia', 'run', 'host', 'press', 'baby', 'gift',
  'gifts', 'promo', 'cash', 'services', 'solutions', 'systems', 'company', 'center', 'network', 'group', 'agency', 'plus',
  'global', 'tips', 'guru',
  'delivery', 'express', 'shopping', 'photos', 'gallery', 'fit', 'fyi', 'ltd',
];
// `tail` is text glued onto the link with no space after a stop character
// (youtube.com/redirect;q=evil.shop); a domain there counts like a query value.
const IP_PATH_EXEMPT_HOSTS = ['bluemark.io', 'dronescout.co', 'gearfocus.b-cdn.net'];
function hasSmuggledHost(match: string, tail: string): boolean {
  const queryAt = match.search(/[?#]/);
  const slashAt = match.indexOf('/');
  const path =
    slashAt >= 0 && (queryAt < 0 || slashAt < queryAt) ?
      match.slice(slashAt + 1, queryAt < 0 ? undefined : queryAt) :
      '';
  const query = queryAt >= 0 ? match.slice(queryAt + 1) : '';
  const hosts: string[] = [];
  // a value is a host when no letter or digit touches it on either side; it may
  // start with "-" or "." ("?q=-evil.shop") and end with a dot ("evil.shop.")
  // The run of leading "-"/"." is taken only from its first character, so a long
  // "-.-." run is scanned once, not once per start position. The run's last
  // character can also be the edge, so a host behind a word ("word.-evil.shop")
  // is still seen (CTO M54 LOW-A).
  const valueRe = () => new RegExp(`(?:^|[^-.\\p{L}\\p{M}\\p{N}]|[-.](?=[\\p{L}\\p{M}\\p{N}]))[-.]*(${HOST_SRC})(?![-\\p{L}\\p{M}\\p{N}])`, 'giu');
  // A path segment's right edge is any non-host character, so a hyphen does
  // not end a domain ("lens.co-op", "insta360.one-x2" are slugs). Raw IP
  // segments do not count on our file and firmware hosts, where four-number
  // version folders are normal (bluemark.io/firmware/2.1.0.3/...).
  const host = match.split(/[/?#:]/)[0].toLowerCase();
  const fileHost = IP_PATH_EXEMPT_HOSTS.some((h) => host === h || host.endsWith('.' + h));
  const pathRe = new RegExp(
    `(?:^|[^\\p{L}\\p{M}\\p{N}-])((?:[\\p{L}\\p{M}\\p{N}][-\\p{L}\\p{M}\\p{N}]*\\.)+(?:${COMMON_TLDS.join('|')}|xn--[a-z0-9-]{1,59}|(?=[\\p{L}]*[^\\x00-\\x7F])[\\p{L}]{2,63})${
      fileHost ? '' : '|(?:\\d{1,3}\\.){3}\\d{1,3}'
    })(?![-\\p{L}\\p{M}\\p{N}])`,
    'giu'
  );
  let m: RegExpExecArray | null;
  for (const [text, skipFiles] of [[query, true], [tail, false]] as const) {
    const re = idnHostRegex(valueRe(), text);
    while ((m = re.exec(text)) !== null) {
      // Query filenames are not redirect hosts; a later real host still counts.
      if (!skipFiles || !FILE_EXTENSIONS.has(m[1].split(".").pop()!.toLowerCase())) hosts.push(m[1]);
    }
  }
  const unicodePathRe = idnHostRegex(pathRe, path);
  while ((m = unicodePathRe.exec(path)) !== null) hosts.push(m[1]);
  // A symbol/punctuation-bearing host has an xn-- spelling, including outside COMMON_TLDS.
  // Keep this extra pass off normal paths and keep ordinary filenames exempt.
  if (IDN_SYMBOL.test(path)) {
    const symbolRe = idnHostRegex(new RegExp(`(?:^|[^\\p{L}\\p{M}\\p{N}-])(${HOST_SRC})(?![-\\p{L}\\p{M}\\p{N}])`, "giu"), path);
    while ((m = symbolRe.exec(path)) !== null) {
      if (IDN_SYMBOL.test(m[1]) && !FILE_EXTENSIONS.has(m[1].split(".").pop()!.toLowerCase())) hosts.push(m[1]);
    }
  }
  return hosts.some((h) => !isAllowlistedUrl(`https://${h.replace(/\.$/, '')}/`));
}

// 16 Ki UTF-16 units allows several thousand words in a chat message. Fixed
// scan work below this ceiling; oversize masking is O(n), with no regex scan.
export const MAX_DETECTION_LENGTH = 16_384;

/**
 * Normalizes message content to defeat encoding-based evasion attacks.
 * Applied BEFORE regex detection so obfuscated content is caught.
 *
 * Order matters:
 * 1. Percent-decode first (reveals encoded zero-width chars and fullwidth chars)
 * 2. Strip zero-width characters (invisible chars that break regex matching)
 * 3. Unicode NFKC normalization (collapses fullwidth, compatibility forms to ASCII)
 */
function normalizeForDetection(content: string): string {
  let normalized = content;

  // 1. Percent-decode — decode valid sequences individually so a single
  //    malformed % (e.g., trailing "path%") doesn't abort decoding of
  //    valid encoded payloads elsewhere in the string.
  //    Loop handles double/triple encoding: %2577orld → %77orld → world
  for (let i = 0; i < 3; i++) {
    if (!/%[0-9A-Fa-f]{2}/.test(normalized)) break;
    // GB #2064: decode a RUN of %XX together so multi-byte characters decode
    // ("evil%E3%80%82shop" -> "evil\u3002shop"); a bad run falls back per byte.
    const decoded = normalized.replace(/(?:%[0-9A-Fa-f]{2})+/g, (run) => {
      try { return decodeURIComponent(run); } catch {
        return run.replace(/%[0-9A-Fa-f]{2}/g, (match) => {
          try { return decodeURIComponent(match); } catch { return match; }
        });
      }
    });
    if (decoded === normalized) break;
    normalized = decoded;
  }

  // 2. Strip zero-width and invisible formatting characters
  //    These can be inserted between letters to break regex matching
  //    GB #2064: also U+2061-2064 (invisible math operators), U+034F, U+3164,
  //    and bidi marks, seen padding scam links and lures.
  normalized = normalized.replace(
    /[\u200B\u200C\u200D\uFEFF\u00AD\u2060\u180E\u2028\u2029\u2061-\u2064\u034F\u3164\u200E\u200F\u202A-\u202E\u2066-\u2069]/g,
    ''
  );
  //    Mongolian variation selectors, the ideographic variation selectors
  //    (U+E0100-E01EF), and shorthand format controls (U+1BCA0-1BCA3): browsers
  //    drop all of them from a domain ("evil" + U+180B + ".shop" opens evil.shop).
  normalized = normalized.replace(
    /[\u180B-\u180D\u180F]|\uDB40[\uDD00-\uDDEF]|\uD82F[\uDCA0-\uDCA3]/g,
    ''
  );

  // 3. Unicode NFKC normalization — collapses fullwidth, superscript,
  //    subscript, and compatibility characters to their standard forms
  //    e.g., fullwidth ｗ (U+FF57) → w, fi ligature (U+FB01) → fi
  normalized = normalized.normalize('NFKC');

  // 4. GB #2064: a variation selector hidden inside a link ("gear\uFE00focus")
  //    is dropped; emoji presentation selectors elsewhere stay. No lookbehind:
  //    Safari < 16.4 cannot parse one.
  normalized = normalized
    .replace(/([a-z0-9./])[\uFE00-\uFE0F]+/gi, '$1')
    .replace(/[\uFE00-\uFE0F]+(?=[a-z0-9./])/gi, '');

  return normalized;
}

// GB #2064: disguised separators. An ideographic full stop (U+3002, which NFKC
// keeps) stands in for "." and a backslash for "/". Each swap is one character
// for one character, so positions line up and restoreSeparators can put the
// writer's original characters back everywhere outside a masked link.
function linkSeparators(text: string): string {
  return text.replace(/[\u3002\uFF61]/g, '.').replace(/\\/g, '/');
}

function restoreSeparators(output: string, base: string, linked: string): string {
  if (output.length !== base.length || linked.length !== base.length) return output;
  let restored = '';
  for (let i = 0; i < output.length; i++) {
    restored += base[i] !== linked[i] && output[i] === linked[i] ? base[i] : output[i];
  }
  return restored;
}


export interface ContactInfoDetectionResult {
  originalContent: string;
  redactedContent: string;
  wasRedacted: boolean;      // True if content was actually changed (redacted)
  shouldAlert: boolean;      // True if any suspicious content detected (even if not redacted)
  detectedTypes: string[];
  warningType?: 'contact_info' | 'payment_app' | 'suspicious_url';  // Which warning to show (if any)
}

/**
 * Detects and redacts contact information from a message
 *
 * REDACTED (blocked): phone numbers, emails, @handles, payment apps, any external URL not on the allowlist
 * ALERT ONLY (not blocked): social platform names
 *
 * Returns the redacted content and info about what was detected
 *
 * GB #2064: `bareLinks` turns on the bare domain + path rule. The chat send
 * path (detectForSender) and offer notes set it; listing descriptions and shop policies
 * (redactPhonesAndEmailsFromStr) keep the http(s)-only rule, per Bill
 * 2026-10-19: "Chat only now; file descriptions separately".
 * Offer and counter-offer notes count as chat (Bill 2026-10-19: "offer notes
 * count as chat"): offers.service.ts calls this with bareLinks: true, matching
 * gearfocus-api's onChatChanged, which scans them as chat messages.
 */
export function detectAndRedactContactInfo(
  content: string,
  options: { bareLinks?: boolean } = {}
): ContactInfoDetectionResult {
  // Normalize content to defeat encoding-based evasion
  const oversizedResult = () => ({
    originalContent: content,
    shouldAlert: true,
    redactedContent: "█".repeat(content.length),
    wasRedacted: true,
    detectedTypes: ["suspicious_url"],
    warningType: "suspicious_url" as const,
  });
  if (options.bareLinks && content.length > MAX_DETECTION_LENGTH) return oversizedResult();
  const baseContent = normalizeForDetection(content);
  if (options.bareLinks && baseContent.length > MAX_DETECTION_LENGTH) return oversizedResult();
  const normalizedContent = linkSeparators(baseContent);

  const detectedTypes: string[] = [];
  const alertOnlyTypes: string[] = [];
  let redactedContent = normalizedContent;  // Work on normalized version

  // === REDACTED CONTENT (actually blocked) ===
  // All detectors scan the full normalized content, and redaction is applied BY
  // POSITION (maskRange) — never string.replace(), which would hit the first
  // matching substring and could redact the wrong occurrence. The ONLY exemption
  // is the social-handle pass skipping a "@handle" that is a legitimate YouTube
  // channel path (youtube.com/@name) — contact info embedded anywhere else in an
  // allowlisted URL is still caught. maskRange is length-preserving, so ranges
  // can be applied in sequence without offsets shifting.

  // Detect phone numbers - REDACT
  const phoneMatches = findPhoneNumbersInText(normalizedContent, 'US');
  if (phoneMatches.length > 0) {
    detectedTypes.push('phone');
    phoneMatches.forEach((match) => {
      redactedContent = maskRange(redactedContent, match.startsAt, match.endsAt);
    });
  }

  // Detect emails - REDACT
  for (const [start, end] of collectMatchRanges(normalizedContent, EMAIL_REGEX)) {
    if (!detectedTypes.includes('email')) detectedTypes.push('email');
    redactedContent = maskRange(redactedContent, start, end);
  }

  // Detect social media handles (@username) - REDACT
  // Exempt only legitimate YouTube channel paths (youtube.com/@name); bare handles
  // and handles anywhere else (incl. elsewhere in an allowlisted URL) are caught.
  for (const [start, end] of collectMatchRanges(
    normalizedContent,
    SOCIAL_HANDLE_REGEX
  )) {
    if (isYouTubeChannelHandle(normalizedContent, start)) continue;
    if (!detectedTypes.includes('social_handle')) detectedTypes.push('social_handle');
    redactedContent = maskRange(redactedContent, start, end);
  }

  // Detect payment apps - REDACT (off-platform payment is risky)
  for (const [start, end] of collectMatchRanges(
    normalizedContent,
    PAYMENT_APPS_REGEX
  )) {
    if (!detectedTypes.includes('payment_app')) detectedTypes.push('payment_app');
    redactedContent = maskRange(redactedContent, start, end);
  }

  // Detect external URLs — REDACT unless allowlisted (gearfocus.com / gearfocus.b-cdn.net)
  // This supersedes the old .world-only BLOCKED_TLD check: any non-allowlisted URL is blocked.
  const urlMatches = normalizedContent.match(options.bareLinks ? idnHostRegex(EXTERNAL_URL_REGEX, normalizedContent) : EXTERNAL_URL_REGEX);
  if (urlMatches && urlMatches.length > 0) {
    const suspiciousUrls = urlMatches.filter((url) => !isAllowlistedUrl(url));

    if (suspiciousUrls.length > 0 && !detectedTypes.includes('suspicious_url')) {
      detectedTypes.push('suspicious_url');
    }
    const uniqueSuspicious = [...new Set(suspiciousUrls)];
    uniqueSuspicious.forEach((url) => {
      redactedContent = redactedContent.split(url).join('█'.repeat(url.length));
    });
  }

  // Match the server's canonical fallback so private proofs accept browser masks.
  // Keep this chat-only: listing descriptions and shop policies retain their rule.
  if (options.bareLinks && !detectedTypes.includes('suspicious_url')) {
    const asciiOnly = normalizedContent.replace(/[^\x00-\x7F]/g, '');
    EXTERNAL_URL_REGEX.lastIndex = 0;
    const asciiUrlMatches = asciiOnly.match(EXTERNAL_URL_REGEX);
    if (asciiUrlMatches?.some((url) => !isAllowlistedUrl(url))) {
      detectedTypes.push('suspicious_url');
      redactedContent = '█'.repeat(redactedContent.length);
    }
  }

  // Bare-domain shortener catch — runs on normalized content with no scheme
  // required. Closes the gap where bots send "tr.ee/xyz" or "tr%2eee/xyz"
  // (decoded to "tr.ee/xyz" by normalization) without an http:// prefix.
  // Runs UNCONDITIONALLY (not guarded by !suspicious_url) to defeat the
  // decoy-URL bypass: "https://evil.com and tr.ee/scam" — without this,
  // the first URL would set the flag and the shortener would slip through.
  // String.split().join() is idempotent, so this is safe to run alongside
  // earlier URL redaction passes.
  SHORTENER_REGEX.lastIndex = 0;
  const shortenerMatches = normalizedContent.match(SHORTENER_REGEX);
  if (shortenerMatches && shortenerMatches.length > 0) {
    if (!detectedTypes.includes('suspicious_url')) {
      detectedTypes.push('suspicious_url');
    }
    const uniqueShorteners = [...new Set(shortenerMatches)];
    uniqueShorteners.forEach((match) => {
      redactedContent = redactedContent.split(match).join('█'.repeat(match.length));
    });
  }

  // GB #2064: bare domain + path catch (chat only, see bareLinks). Within chat it
  // is unconditional for the same decoy reason as the shortener pass. Allowlisted hosts (gearfocus.com/m/..., youtube.com/...)
  // pass through. Masked by position, so it is safe after the passes above.
  const bareRe = idnHostRegex(BARE_DOMAIN_PATH_REGEX, normalizedContent);
  let bare: RegExpExecArray | null;
  while (options.bareLinks && (bare = bareRe.exec(normalizedContent)) !== null) {
    const match = bare[2];
    const start = bare.index + bare[1].length;
    const end = start + match.length;
    if (isAllowlistedUrl(`https://${match}`)) {
      // Text glued on after a stop character belongs to the same token.
      let tokenEnd = end;
      while (tokenEnd < normalizedContent.length && !/[\s<>"']/.test(normalizedContent[tokenEnd])) {
        tokenEnd++;
      }
      if (!hasSmuggledHost(match, normalizedContent.slice(end, tokenEnd))) {
        // Already checked whole: resume after the token, never rescan it.
        bareRe.lastIndex = tokenEnd;
        continue;
      }
      // A smuggled host: mask the whole token, then resume after it.
      if (!detectedTypes.includes('suspicious_url')) detectedTypes.push('suspicious_url');
      redactedContent = maskRange(redactedContent, start, tokenEnd);
      bareRe.lastIndex = tokenEnd;
      continue;
    }
    if (!detectedTypes.includes('suspicious_url')) detectedTypes.push('suspicious_url');
    redactedContent = maskRange(redactedContent, start, end);
  }

  // Detect competitor / off-platform sale sites (GB #2055) - REDACT, contact_info banner
  // Runs AFTER the URL passes: they redact by string split() and would miss a URL
  // already altered here (e.g. https://evil.com/offerup/x); maskRange is
  // length-preserving, so masking over an already-blocked URL is harmless.
  for (const [start, end] of collectMatchRanges(
    normalizedContent,
    OFFPLATFORM_SITES_REGEX
  )) {
    if (!detectedTypes.includes('offplatform_site')) detectedTypes.push('offplatform_site');
    redactedContent = maskRange(redactedContent, start, end);
  }

  // === ALERT ONLY (not blocked, just flagged for review) ===

  // Detect social media platform mentions - ALERT ONLY
  const platformMatches = normalizedContent.match(SOCIAL_PLATFORM_REGEX);
  if (platformMatches && platformMatches.length > 0) {
    alertOnlyTypes.push('social_platform');
  }

  // GB #2055: eBay / KEH / MPB / "text me" / "call me" / "meet up" - ALERT ONLY
  if (OFFPLATFORM_ALERT_REGEX.test(normalizedContent)) {
    alertOnlyTypes.push('offplatform_mention');
  }

  const allDetectedTypes = [...detectedTypes, ...alertOnlyTypes];

  // Determine which warning to show (suspicious URL highest priority — active fraud)
  let warningType: 'contact_info' | 'payment_app' | 'suspicious_url' | undefined;
  if (detectedTypes.includes('suspicious_url')) {
    warningType = 'suspicious_url';
  } else if (detectedTypes.includes('payment_app')) {
    warningType = 'payment_app';
  } else if (detectedTypes.length > 0) {
    warningType = 'contact_info';
  }

  // If nothing was detected, preserve original content unchanged
  // Only use normalized+redacted content when detection triggered
  const finalRedactedContent =
    detectedTypes.length > 0
      ? restoreSeparators(redactedContent, baseContent, normalizedContent)
      : content;

  return {
    originalContent: content,
    redactedContent: finalRedactedContent,
    wasRedacted: detectedTypes.length > 0,           // Only true if we actually changed content
    shouldAlert: allDetectedTypes.length > 0,        // True if anything suspicious detected
    detectedTypes: allDetectedTypes,
    warningType,
  };
}

/**
 * GB #1942: detectAndRedactContactInfo, except exempt senders (see
 * isMaskingExempt) get their content back unchanged with nothing flagged, so
 * no alert, banner, or chat flag follows. Used by every send path.
 */
export function detectForSender(
  content: string,
  senderRoles: Parameters<typeof isMaskingExempt>[0]
): ContactInfoDetectionResult {
  if (isMaskingExempt(senderRoles)) {
    return {
      originalContent: content,
      redactedContent: content,
      wasRedacted: false,
      shouldAlert: false,
      detectedTypes: [],
      warningType: undefined,
    };
  }
  return detectAndRedactContactInfo(content, { bareLinks: true });
}

/**
 * Adds a GearBot safety warning message to the chat
 * @param warningType - 'contact_info' for phone/email/@handles, 'payment_app' for venmo/zelle/etc.
 */
export async function addSafetyWarningToChat(
  chat: ChatDocument,
  warningType: 'contact_info' | 'payment_app' | 'suspicious_url' = 'contact_info'
): Promise<void> {
  if (!chat.id) return;

  const warningContent = warningType === 'suspicious_url'
    ? SUSPICIOUS_URL_WARNING
    : warningType === 'payment_app'
      ? PAYMENT_APP_WARNING
      : CONTACT_INFO_WARNING;

  const now = Date.now();
  const warningMessage = ChatMessagesDocumentSchema.parse({
    uid: 'GearBot',
    content: warningContent,
    is_img: false,
    created_at: now,
  });

  // Get current messages and add the warning
  const messages = chat.messages ? [...chat.messages, warningMessage] : [warningMessage];

  const chatRef = doc(db, 'messages', chat.id);
  await setDoc(
    chatRef,
    {
      messages,
      last_time: now,
    },
    { merge: true }
  );
}

/**
 * GB #2064: adds the GearBot safety banner and flags the chat by APPENDING, so a
 * caller that does not hold the chat's full current message list (offer and
 * counter-offer notes) can never overwrite messages written since.
 */
export async function appendSafetyWarningToChat(
  chatId: string,
  warningType: 'contact_info' | 'payment_app' | 'suspicious_url' = 'contact_info'
): Promise<void> {
  const now = Date.now();
  const warningMessage = ChatMessagesDocumentSchema.parse({
    uid: 'GearBot',
    content:
      warningType === 'suspicious_url'
        ? SUSPICIOUS_URL_WARNING
        : warningType === 'payment_app'
          ? PAYMENT_APP_WARNING
          : CONTACT_INFO_WARNING,
    is_img: false,
    created_at: now,
    // Like the server's banner: never notify anyone about the banner itself.
    skip_notification: true,
  });
  await updateDoc(doc(db, 'messages', chatId), {
    messages: arrayUnion(warningMessage),
    last_time: now,
    flagged: true,
  });
}

/**
 * Sends a Slack alert about contact info detection via API route
 * (Direct Slack webhook calls from browser fail due to CORS)
 *
 * @param participantNames - Optional array of participant display names (preferred over UIDs in alert)
 */
export async function sendContactInfoSlackAlert(
  chat: ChatDocument,
  senderUid: string,
  originalContent: string,
  detectedTypes: string[],
  participantNames?: string[]
): Promise<void> {
  const payload = {
    chatId: chat.id || 'N/A',
    senderUid,
    originalContent,
    detectedTypes,
    participants: chat.uids,
    participantNames,
  };

  try {
    await fetch(CONTACT_INFO_ALERT_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error('Failed to send contact info Slack alert:', error);
  }
}

/**
 * Handles contact info detection: redacts content, sends alerts, adds warning message
 * Call this when processing a message before saving
 *
 * - If content was REDACTED: Send Slack alert + safety warning to chat
 * - If ALERT ONLY detected (URLs, platform names): Send Slack alert only (no warning in chat)
 *
 * @param participantNames - Optional array of participant display names for the Slack alert
 */
export async function handleContactInfoDetection(
  content: string,
  chat: ChatDocument,
  senderUid: string,
  participantNames?: string[],
  senderRoles?: Parameters<typeof isMaskingExempt>[0]
): Promise<ContactInfoDetectionResult> {
  // GB #1942: exempt senders pass through untouched (no redaction, no Slack
  // alert, no safety banner). Non-exempt behavior is unchanged.
  const result = detectForSender(content, senderRoles);

  if (result.shouldAlert && chat.id) {
    // Always send Slack alert if anything suspicious detected
    sendContactInfoSlackAlert(chat, senderUid, result.originalContent, result.detectedTypes, participantNames)
      .catch((error) => console.error('Error sending Slack alert:', error));

    // Only add safety warning to chat if content was actually redacted
    if (result.wasRedacted && result.warningType) {
      addSafetyWarningToChat(chat, result.warningType)
        .catch((error) => console.error('Error adding safety warning:', error));
    }
  }

  return result;
}
